import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Truck, Container, Factory, Plus, Pencil, Archive, ArchiveRestore, Search, X, Phone, MapPin, Gauge, Ban, ShieldAlert } from 'lucide-react';
import { PageShell, glassLight, primaryButton, ghostButton, inputStyle, EmptyState, Field, MotionGrid, MotionItem, CampoValidado, useConfirmacion, useIsMobile, T } from '../ui';
import type { Camion, Cola, Conductor, Envasadora } from '../types';

// Catálogos que alimentan el formulario de orden de pedido. Van juntos en un
// módulo porque se mantienen juntos: un conductor sin su camión y su cola no
// sirve de nada, y una orden sin envasadoras no tiene a dónde ir.
//
// Aquí también viven las reglas del despacho —qué cola entra en qué planta,
// qué camión no puede ir a dónde— que antes solo estaban en una hoja de Excel
// que alguien tenía que recordar.

type TabFlota = 'conductores' | 'camiones' | 'colas' | 'envasadoras';

// El singular va explícito: quitarle la "s" al plural produce "conductore".
const TABS: {
  id: TabFlota;
  label: string;
  singular: string;
  nuevo: string;
  icono: React.ElementType;
  color: string;
  soft: string;
}[] = [{
  id: 'conductores',
  label: 'Conductores',
  singular: 'conductor',
  nuevo: 'Nuevo conductor',
  icono: User,
  color: '#FF6B01',
  soft: 'rgba(255,107,1,.12)'
}, {
  id: 'camiones',
  label: 'Camiones',
  singular: 'camión',
  nuevo: 'Nuevo camión',
  icono: Truck,
  color: '#2563eb',
  soft: 'rgba(37,99,235,.12)'
}, {
  id: 'colas',
  label: 'Colas',
  singular: 'cola',
  nuevo: 'Nueva cola',
  icono: Container,
  color: '#7c3aed',
  soft: 'rgba(124,58,237,.12)'
}, {
  id: 'envasadoras',
  label: 'Envasadoras',
  singular: 'envasadora',
  nuevo: 'Nueva envasadora',
  icono: Factory,
  color: '#16a34a',
  soft: 'rgba(22,163,74,.12)'
}];
const fmtGal = (n: number) => new Intl.NumberFormat('es-DO').format(n || 0);
const isoLocal = (d: Date) => {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};

// Un solo objeto de formulario para las cuatro pestañas: los campos que no
// aplican simplemente se ignoran al guardar. Tener cuatro estados separados
// solo repetiría el mismo abrir/cerrar/limpiar cuatro veces.
interface FormFlota {
  nombre: string;
  pase: string;
  telefono: string;
  // Un conductor puede tener varios camiones y varias colas, así que ambos son
  // listas. Un renglón recién agregado está vacío hasta que se elige.
  camionIds: string[];
  colaIds: string[];
  placa: string;
  plantasVetadas: string[];
  soloConductorId: string;
  ficha: string;
  capacidad: string;
  descripcion: string;
  ciudad: string;
  rnc: string;
  direccion: string;
  colasPermitidas: string[];
  colasSegundaOpcion: string[];
}
const FORM_VACIO: FormFlota = {
  nombre: '',
  pase: '',
  telefono: '',
  camionIds: [],
  colaIds: [],
  placa: '',
  plantasVetadas: [],
  soloConductorId: '',
  ficha: '',
  capacidad: '',
  descripcion: '',
  ciudad: '',
  rnc: '',
  direccion: '',
  colasPermitidas: [],
  colasSegundaOpcion: []
};

// Lista de renglones con su botón "+". El "+" va abajo y a la izquierda,
// alineado con los renglones, para que se lea como "y uno más" y no como una
// acción sobre el bloque entero.
function BloqueLista({
  label,
  valores,
  vacio,
  onAgregar,
  onQuitar,
  textoAgregar,
  render
}: {
  label: string;
  valores: string[];
  vacio: string;
  onAgregar: () => void;
  onQuitar: (i: number) => void;
  textoAgregar: string;
  render: (valor: string, i: number) => React.ReactNode;
}) {
  return <div data-id="element-575" data-mp="uxuet28d81k4">
      <div style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '.05em',
      textTransform: 'uppercase',
      color: T.textMuted,
      marginBottom: 8
    }} data-id="element-576" data-mp="q0vjwhgvnqkv">

        {label}
      </div>

      {valores.length === 0 ? <div style={{
      fontSize: 12,
      color: T.textMuted,
      marginBottom: 9
    }} data-id="element-577" data-mp="1ip9lf776d516">
          {vacio}
        </div> : <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      marginBottom: 9
    }} data-id="element-578" data-mp="128gwnn7zzzh8">

          {valores.map((v, i) => <div key={i} style={{
        display: 'flex',
        gap: 7
      }} data-id="element-579" data-mp="t9low6pb2hbt" data-mpi={i}>
              <div style={{
          flex: 1,
          minWidth: 0
        }} data-id="element-580" data-mp="tboptlrzxuvb">{render(v, i)}</div>
              <button type="button" onClick={() => onQuitar(i)} title="Quitar" style={{
          ...ghostButton,
          flexShrink: 0,
          padding: '0 11px',
          color: T.textMuted
        }} data-id="element-581" data-mp="ya9ohgrlo6hn">

                <X size={14} data-id="element-582" data-mp="1c87u9xruuulj" />
              </button>
            </div>)}
        </div>}

      <button type="button" onClick={onAgregar} style={{
      ...ghostButton,
      padding: '7px 13px',
      fontSize: 12.5
    }} data-id="element-583" data-mp="1c6dfl176fypw">

        <Plus size={14} data-id="element-584" data-mp="24wlpk5rk7cte" /> {textoAgregar}
      </button>
    </div>;
}

// Selección múltiple en fichas. Para listas cortas y conocidas (seis colas,
// diez plantas) esto se lee de un vistazo; un desplegable múltiple obliga a
// abrirlo para saber qué hay marcado.
function Fichas({
  label,
  ayuda,
  opciones,
  seleccion,
  onToggle,
  color = T.accent
}: {
  label: string;
  ayuda?: string;
  opciones: {
    id: string;
    texto: string;
  }[];
  seleccion: string[];
  onToggle: (id: string) => void;
  color?: string;
}) {
  return <div data-id="element-585" data-mp="2ntnko59goiha">
      <div style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '.05em',
      textTransform: 'uppercase',
      color: T.textMuted,
      marginBottom: ayuda ? 4 : 8
    }} data-id="element-586" data-mp="2ntvvzspxrfqd">

        {label}
      </div>
      {ayuda ? <div style={{
      fontSize: 11.5,
      color: T.textMuted,
      marginBottom: 9
    }} data-id="element-587" data-mp="2a1fs6pp7ndoe">
          {ayuda}
        </div> : null}
      {opciones.length === 0 ? <div style={{
      fontSize: 12,
      color: T.textMuted
    }} data-id="element-588" data-mp="251b9qu4qs8vj">
          Nada que elegir todavía.
        </div> : <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 7
    }} data-id="element-589" data-mp="3lipbnc8ca5pp">
          {opciones.map(o => {
        const marcada = seleccion.includes(o.id);
        return <button key={o.id} type="button" onClick={() => onToggle(o.id)} style={{
          padding: '7px 13px',
          borderRadius: 99,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          color: marcada ? color : T.textSecondary,
          background: marcada ? `${color}18` : 'transparent',
          border: `1px solid ${marcada ? `${color}55` : T.border}`,
          transition: 'all .12s'
        }} data-id="element-590" data-mp="3oi7kjcrb3ga6">

                {o.texto}
              </button>;
      })}
        </div>}
    </div>;
}

// Fila de dato dentro de una tarjeta: ícono tenue a la izquierda, texto que
// se parte si no cabe. Sin ícono el dato queda flotando sin jerarquía.
function Dato({
  icono: Icono,
  children
}: {
  icono: React.ElementType;
  children: React.ReactNode;
}) {
  return <div style={{
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start'
  }} data-id="element-591" data-mp="2r21s2bdxxu37">
      <Icono size={13} style={{
      color: T.textMuted,
      flexShrink: 0,
      marginTop: 2.5
    }} data-id="element-592" data-mp="2jk3v16cy4bgv" />
      <span style={{
      lineHeight: 1.45,
      wordBreak: 'break-word',
      minWidth: 0
    }} data-id="element-593" data-mp="2ek7q94wfcg09">
        {children}
      </span>
    </div>;
}
interface FlotaProps {
  camiones: Camion[];
  colas: Cola[];
  envasadoras: Envasadora[];
  conductores: Conductor[];
  onAddCamion: (c: Camion) => void;
  onUpdateCamion: (c: Camion) => void;
  onArchivarCamion: (c: Camion, archivar: boolean) => void;
  onAddCola: (c: Cola) => void;
  onUpdateCola: (c: Cola) => void;
  onArchivarCola: (c: Cola, archivar: boolean) => void;
  onAddEnvasadora: (e: Envasadora) => void;
  onUpdateEnvasadora: (e: Envasadora) => void;
  onArchivarEnvasadora: (e: Envasadora, archivar: boolean) => void;
  onAddConductor: (c: Conductor) => void;
  onUpdateConductor: (c: Conductor) => void;
  onArchivarConductor: (c: Conductor, archivar: boolean) => void;
  // Registros que se guardaron en el navegador pero no llegaron a Supabase.
  pendientes: number;
  canEdit: boolean;
  canArchive: boolean;
  onLog?: (mensaje: string) => void;
}
export function Flota({
  // Igual que en Pedidos: si un catálogo llega sin definir, el módulo se
  // comporta como si estuviera vacío en vez de tumbar la pantalla.
  camiones = [],
  colas = [],
  envasadoras = [],
  conductores = [],
  onAddCamion,
  onUpdateCamion,
  onArchivarCamion,
  onAddCola,
  onUpdateCola,
  onArchivarCola,
  onAddEnvasadora,
  onUpdateEnvasadora,
  onArchivarEnvasadora,
  onAddConductor,
  onUpdateConductor,
  onArchivarConductor,
  pendientes = 0,
  canEdit,
  canArchive,
  onLog
}: FlotaProps) {
  const isMobile = useIsMobile();
  const [dialogo, confirmar] = useConfirmacion();
  const [tab, setTab] = useState<TabFlota>('conductores');
  const [verArchivados, setVerArchivados] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormFlota>(FORM_VACIO);
  const [error, setError] = useState('');
  const conf = TABS.find(t => t.id === tab)!;
  const activo = (x: {
    activo?: boolean;
  }) => x.activo !== false;
  // Solo se ofrecen unidades en servicio al asignar: una archivada no debe
  // volver a entrar en una orden nueva.
  const camionesActivos = camiones.filter(activo);
  const colasActivas = colas.filter(activo);
  const envasadorasActivas = envasadoras.filter(activo);
  const conductoresActivos = conductores.filter(activo);
  const nombreCola = (id: string) => colas.find(c => c.id === id)?.ficha ?? '';
  const nombrePlanta = (id: string) => envasadoras.find(e => e.id === id)?.nombre ?? '';

  // Los contadores de las pestañas siguen la vista: en "Archivados" cuentan
  // archivados. Si no, decían 5 y salía uno solo.
  const enVista = (lista: {
    activo?: boolean;
  }[]) => lista.filter(x => verArchivados ? !activo(x) : activo(x)).length;
  const conteos: Record<TabFlota, number> = {
    conductores: enVista(conductores),
    camiones: enVista(camiones),
    colas: enVista(colas),
    envasadoras: enVista(envasadoras)
  };
  const totalArchivados = camiones.filter(x => !activo(x)).length + colas.filter(x => !activo(x)).length + envasadoras.filter(x => !activo(x)).length + conductores.filter(x => !activo(x)).length;
  const cerrarForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(FORM_VACIO);
    setError('');
  };
  const cambiarTab = (id: TabFlota) => {
    setTab(id);
    setBusqueda('');
    cerrarForm();
  };
  const abrirNuevo = () => {
    setForm(FORM_VACIO);
    setEditingId(null);
    setError('');
    setShowForm(true);
  };
  const set = (patch: Partial<FormFlota>) => {
    setForm(prev => ({
      ...prev,
      ...patch
    }));
    setError('');
  };
  const alternar = (campo: 'plantasVetadas' | 'colasPermitidas' | 'colasSegundaOpcion', id: string) => {
    setForm(prev => {
      const lista = prev[campo];
      const next = lista.includes(id) ? lista.filter(x => x !== id) : [...lista, id];
      // Una cola no puede ser a la vez primera y segunda opción: marcarla en
      // una la quita de la otra.
      if (campo === 'colasPermitidas') {
        return {
          ...prev,
          colasPermitidas: next,
          colasSegundaOpcion: prev.colasSegundaOpcion.filter(x => x !== id)
        };
      }
      if (campo === 'colasSegundaOpcion') {
        return {
          ...prev,
          colasSegundaOpcion: next,
          colasPermitidas: prev.colasPermitidas.filter(x => x !== id)
        };
      }
      return {
        ...prev,
        [campo]: next
      };
    });
    setError('');
  };

  // Helpers de las listas del conductor. Un renglón nuevo entra vacío y se
  // elige después: agregar y elegir son dos gestos distintos.
  const agregarEn = (campo: 'camionIds' | 'colaIds') => {
    setForm(prev => ({
      ...prev,
      [campo]: [...prev[campo], '']
    }));
    setError('');
  };
  const quitarDe = (campo: 'camionIds' | 'colaIds', i: number) => {
    setForm(prev => ({
      ...prev,
      [campo]: prev[campo].filter((_, k) => k !== i)
    }));
    setError('');
  };
  const fijarEn = (campo: 'camionIds' | 'colaIds', i: number, valor: string) => {
    setForm(prev => ({
      ...prev,
      [campo]: prev[campo].map((v, k) => k === i ? valor : v)
    }));
    setError('');
  };

  // ── LISTADO DE LA PESTAÑA ACTIVA ──────────────────────────────────────
  const items = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const coincide = (...campos: string[]) => !q || campos.some(c => (c ?? '').toLowerCase().includes(q));
    const enPestana = (x: {
      activo?: boolean;
    }) => verArchivados ? x.activo === false : x.activo !== false;
    if (tab === 'conductores') {
      return conductores.filter(enPestana).filter(c => coincide(c.nombre, c.pase, (c.camiones ?? []).map(k => k.placa).join(' '), (c.colas ?? []).map(k => k.ficha).join(' '))).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    }
    if (tab === 'camiones') {
      return camiones.filter(enPestana).filter(c => coincide(c.nombre, c.placa)).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    }
    if (tab === 'colas') {
      return colas.filter(enPestana).filter(c => coincide(c.ficha, c.descripcion)).sort((a, b) => a.ficha.localeCompare(b.ficha, 'es'));
    }
    return envasadoras.filter(enPestana).filter(e => coincide(e.nombre, e.ciudad, e.rnc, e.direccion, e.telefono)).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [tab, busqueda, verArchivados, conductores, camiones, colas, envasadoras]);

  // ── EDITAR ────────────────────────────────────────────────────────────
  const editar = (id: string) => {
    setEditingId(id);
    setError('');
    setShowForm(true);
    if (tab === 'conductores') {
      const c = conductores.find(x => x.id === id);
      if (!c) return;
      setForm({
        ...FORM_VACIO,
        nombre: c.nombre,
        pase: c.pase,
        telefono: c.telefono,
        camionIds: (c.camiones ?? []).map(k => k.id),
        colaIds: (c.colas ?? []).map(k => k.id)
      });
    } else if (tab === 'camiones') {
      const c = camiones.find(x => x.id === id);
      if (!c) return;
      setForm({
        ...FORM_VACIO,
        nombre: c.nombre,
        placa: c.placa,
        plantasVetadas: c.plantasVetadas ?? [],
        soloConductorId: c.soloConductorId || ''
      });
    } else if (tab === 'colas') {
      const c = colas.find(x => x.id === id);
      if (!c) return;
      setForm({
        ...FORM_VACIO,
        ficha: c.ficha,
        capacidad: c.capacidadGalones ? String(c.capacidadGalones) : '',
        descripcion: c.descripcion
      });
    } else {
      const e = envasadoras.find(x => x.id === id);
      if (!e) return;
      setForm({
        ...FORM_VACIO,
        nombre: e.nombre,
        ciudad: e.ciudad,
        rnc: e.rnc,
        direccion: e.direccion,
        telefono: e.telefono,
        colasPermitidas: e.colasPermitidas ?? [],
        colasSegundaOpcion: e.colasSegundaOpcion ?? []
      });
    }
  };

  // ── GUARDAR ───────────────────────────────────────────────────────────
  const guardar = async () => {
    const hoy = isoLocal(new Date());
    const repetido = (lista: {
      id: string;
    }[], igual: (x: any) => boolean) => lista.some(x => igual(x) && x.id !== editingId);
    const previo = (lista: {
      id: string;
      fecha_registro: string;
    }[]) => editingId ? lista.find(x => x.id === editingId)?.fecha_registro || hoy : hoy;
    if (tab === 'conductores') {
      const nombre = form.nombre.trim();
      if (!nombre) return setError('Escribe el nombre del conductor.');
      if (repetido(conductores, (x: Conductor) => x.nombre.trim().toLowerCase() === nombre.toLowerCase())) {
        return setError('Ese conductor ya está registrado.');
      }
      // Los renglones vacíos y los repetidos se descartan al guardar: es más
      // amable que regañar por un renglón que se dejó sin llenar.
      const unicos = (ids: string[]) => Array.from(new Set(ids.filter(Boolean)));
      const base: Conductor = {
        id: editingId ?? Date.now().toString(),
        nombre,
        pase: form.pase.trim(),
        telefono: form.telefono.trim(),
        // La placa y la ficha se copian como texto para que la orden no pierda
        // el dato si más adelante se da de baja el camión o la cola.
        camiones: unicos(form.camionIds).map(id => camionesActivos.find(x => x.id === id)).filter(Boolean).map(c => ({
          id: c!.id,
          placa: c!.placa
        })),
        colas: unicos(form.colaIds).map(id => colasActivas.find(x => x.id === id)).filter(Boolean).map(c => ({
          id: c!.id,
          ficha: c!.ficha
        })),
        activo: true,
        fecha_registro: previo(conductores)
      };
      const ok = await confirmar({
        titulo: editingId ? '¿Guardar los cambios?' : '¿Registrar este conductor?',
        mensaje: 'Aparecerá en el desplegable al armar una orden. Si no le asignas unidades, podrá llevar cualquiera de las que estén en servicio.',
        detalles: [{
          etiqueta: 'Conductor',
          valor: base.nombre
        }, {
          etiqueta: 'Pase',
          valor: base.pase || '—'
        }, {
          etiqueta: base.camiones.length === 1 ? 'Camión' : 'Camiones',
          valor: base.camiones.map(c => c.placa).join(', ') || 'Cualquiera'
        }, {
          etiqueta: base.colas.length === 1 ? 'Cola' : 'Colas',
          valor: base.colas.map(c => c.ficha).join(', ') || 'Cualquiera'
        }],
        confirmar: editingId ? 'Guardar cambios' : 'Registrar',
        tono: 'accent'
      });
      if (!ok) return;
      if (editingId) {
        onUpdateConductor(base);
        onLog?.(`Conductor editado: ${base.nombre}`);
      } else {
        onAddConductor(base);
        onLog?.(`Conductor registrado: ${base.nombre}`);
      }
    } else if (tab === 'camiones') {
      const nombre = form.nombre.trim();
      const placa = form.placa.trim().toUpperCase();
      if (!nombre) return setError('Escribe el nombre del camión (por ejemplo, MACK VISION).');
      if (!placa) return setError('Escribe la placa del camión.');
      if (repetido(camiones, (x: Camion) => x.placa.trim().toUpperCase() === placa)) {
        return setError('Ya hay un camión registrado con esa placa.');
      }
      const chofer = conductoresActivos.find(x => x.id === form.soloConductorId);
      const base: Camion = {
        id: editingId ?? Date.now().toString(),
        nombre,
        placa,
        plantasVetadas: form.plantasVetadas,
        soloConductorId: chofer?.id ?? '',
        soloConductorNombre: chofer?.nombre ?? '',
        activo: true,
        fecha_registro: previo(camiones)
      };
      const ok = await confirmar({
        titulo: editingId ? '¿Guardar los cambios?' : '¿Registrar este camión?',
        mensaje: 'Al armar una orden solo se ofrecerá para los destinos donde puede entrar.',
        detalles: [{
          etiqueta: 'Camión',
          valor: base.nombre
        }, {
          etiqueta: 'Placa',
          valor: base.placa
        }, {
          etiqueta: 'No puede ir a',
          valor: base.plantasVetadas.map(nombrePlanta).filter(Boolean).join(', ') || 'Ninguna restricción'
        }, {
          etiqueta: 'Solo lo lleva',
          valor: base.soloConductorNombre || 'Cualquier conductor'
        }],
        confirmar: editingId ? 'Guardar cambios' : 'Registrar',
        tono: 'accent'
      });
      if (!ok) return;
      if (editingId) {
        onUpdateCamion(base);
        onLog?.(`Camión editado: ${base.nombre} (${base.placa})`);
      } else {
        onAddCamion(base);
        onLog?.(`Camión registrado: ${base.nombre} (${base.placa})`);
      }
    } else if (tab === 'colas') {
      const ficha = form.ficha.trim().toUpperCase();
      if (!ficha) return setError('Escribe la ficha de la cola.');
      if (repetido(colas, (x: Cola) => x.ficha.trim().toUpperCase() === ficha)) {
        return setError('Esa cola ya está registrada.');
      }
      const base: Cola = {
        id: editingId ?? Date.now().toString(),
        ficha,
        capacidadGalones: Number(form.capacidad) || 0,
        descripcion: form.descripcion.trim(),
        activo: true,
        fecha_registro: previo(colas)
      };
      const ok = await confirmar({
        titulo: editingId ? '¿Guardar los cambios?' : '¿Registrar esta cola?',
        mensaje: 'Sus galones son los que entran solos en la orden cuando se elige esta cola.',
        detalles: [{
          etiqueta: 'Ficha',
          valor: base.ficha
        }, {
          etiqueta: 'Galones',
          valor: base.capacidadGalones ? fmtGal(base.capacidadGalones) : '—'
        }],
        confirmar: editingId ? 'Guardar cambios' : 'Registrar',
        tono: 'accent'
      });
      if (!ok) return;
      if (editingId) {
        onUpdateCola(base);
        onLog?.(`Cola editada: ${base.ficha}`);
      } else {
        onAddCola(base);
        onLog?.(`Cola registrada: ${base.ficha}`);
      }
    } else {
      const nombre = form.nombre.trim();
      if (!nombre) return setError('Escribe el nombre de la envasadora.');
      if (repetido(envasadoras, (x: Envasadora) => x.nombre.trim().toLowerCase() === nombre.toLowerCase())) {
        return setError('Esa envasadora ya está registrada.');
      }
      const base: Envasadora = {
        id: editingId ?? Date.now().toString(),
        nombre,
        ciudad: form.ciudad.trim(),
        rnc: form.rnc.trim(),
        direccion: form.direccion.trim(),
        telefono: form.telefono.trim(),
        colasPermitidas: form.colasPermitidas,
        colasSegundaOpcion: form.colasSegundaOpcion,
        activo: true,
        fecha_registro: previo(envasadoras)
      };
      const ok = await confirmar({
        titulo: editingId ? '¿Guardar los cambios?' : '¿Registrar esta envasadora?',
        mensaje: 'Es uno de los destinos que se pueden elegir al armar una orden. Su nombre y su RNC son los que se imprimen.',
        detalles: [{
          etiqueta: 'Envasadora',
          valor: base.nombre
        }, {
          etiqueta: 'Ciudad',
          valor: base.ciudad || '—'
        }, {
          etiqueta: 'RNC',
          valor: base.rnc || '—'
        }, {
          etiqueta: 'Colas que admite',
          valor: base.colasPermitidas.length ? base.colasPermitidas.map(nombreCola).filter(Boolean).join(', ') : 'Todas'
        }, {
          etiqueta: 'Segunda opción',
          valor: base.colasSegundaOpcion.map(nombreCola).filter(Boolean).join(', ') || '—'
        }],
        confirmar: editingId ? 'Guardar cambios' : 'Registrar',
        tono: 'accent'
      });
      if (!ok) return;
      if (editingId) {
        onUpdateEnvasadora(base);
        onLog?.(`Envasadora editada: ${base.nombre}`);
      } else {
        onAddEnvasadora(base);
        onLog?.(`Envasadora registrada: ${base.nombre}`);
      }
    }
    cerrarForm();
  };

  // ── ARCHIVAR ──────────────────────────────────────────────────────────
  // Nunca se borra: archivar saca el registro de servicio y lo manda a la
  // pestaña "Archivados". Las órdenes ya firmadas con esa unidad no cambian,
  // y siempre se puede devolver a servicio.
  const archivar = async (titulo: string, aviso: string, estaArchivado: boolean, aplicar: () => void) => {
    const ok = await confirmar({
      titulo: estaArchivado ? `¿Devolver ${titulo} a servicio?` : `¿Archivar ${titulo}?`,
      mensaje: estaArchivado ? 'Vuelve a aparecer en los desplegables al armar una orden.' : aviso,
      confirmar: estaArchivado ? 'Sí, devolver a servicio' : 'Sí, archivar',
      tono: estaArchivado ? 'accent' : 'warn'
    });
    if (!ok) return;
    aplicar();
    onLog?.(estaArchivado ? `${conf.singular} devuelto a servicio: ${titulo}` : `${conf.singular} archivado: ${titulo}`);
  };

  // ── TARJETAS ──────────────────────────────────────────────────────────
  const tarjeta = (x: any) => {
    if (tab === 'conductores') {
      const c = x as Conductor;
      return {
        id: c.id,
        titulo: c.nombre,
        etiqueta: c.pase || 'Sin pase',
        filas: [{
          icono: Truck,
          texto: (c.camiones ?? []).map(k => k.placa).join(' · ') || 'Cualquier camión en servicio'
        }, {
          icono: Container,
          texto: (c.colas ?? []).map(k => k.ficha).join(' · ') || 'Cualquier cola en servicio'
        }, ...(c.telefono ? [{
          icono: Phone,
          texto: c.telefono
        }] : [])],
        archivado: c.activo === false,
        alArchivar: () => archivar(c.nombre, 'Deja de aparecer en el desplegable de las órdenes. Las que ya lo tienen registrado no cambian, y puedes devolverlo a servicio cuando quieras.', c.activo === false, () => onArchivarConductor(c, c.activo !== false))
      };
    }
    if (tab === 'camiones') {
      const c = x as Camion;
      const vetos = (c.plantasVetadas ?? []).map(nombrePlanta).filter(Boolean);
      return {
        id: c.id,
        titulo: c.nombre || c.placa,
        etiqueta: c.placa || 'Sin placa',
        filas: [{
          icono: vetos.length ? Ban : Truck,
          texto: vetos.length ? `No puede ir a: ${vetos.join(', ')}` : 'Puede ir a cualquier planta'
        }, ...(c.soloConductorNombre ? [{
          icono: ShieldAlert,
          texto: `Solo lo lleva ${c.soloConductorNombre}`
        }] : [])],
        archivado: c.activo === false,
        alArchivar: () => archivar(c.nombre || c.placa, 'Sale de servicio: deja de ofrecerse al asignar y se quita de los conductores que lo tengan. Las órdenes ya firmadas con él no cambian.', c.activo === false, () => onArchivarCamion(c, c.activo !== false))
      };
    }
    if (tab === 'colas') {
      const c = x as Cola;
      const asignados = conductores.filter(k => (k.colas ?? []).some(u => u.id === c.id)).length;
      return {
        id: c.id,
        titulo: c.ficha,
        etiqueta: c.capacidadGalones ? `${fmtGal(c.capacidadGalones)} gal` : 'Sin galones',
        filas: [{
          icono: Gauge,
          texto: c.capacidadGalones ? `${fmtGal(c.capacidadGalones)} galones por viaje` : 'Galones sin registrar'
        }, {
          icono: User,
          texto: asignados ? `${asignados} conductor${asignados > 1 ? 'es' : ''} asignado${asignados > 1 ? 's' : ''}` : 'Sin conductor fijo'
        }, ...(c.descripcion ? [{
          icono: Container,
          texto: c.descripcion
        }] : [])],
        archivado: c.activo === false,
        alArchivar: () => archivar(c.ficha, 'Sale de servicio: deja de ofrecerse al asignar y se quita de los conductores que la tengan. Las órdenes ya firmadas con ella no cambian.', c.activo === false, () => onArchivarCola(c, c.activo !== false))
      };
    }
    const e = x as Envasadora;
    const permitidas = (e.colasPermitidas ?? []).map(nombreCola).filter(Boolean);
    const segundas = (e.colasSegundaOpcion ?? []).map(nombreCola).filter(Boolean);
    return {
      id: e.id,
      titulo: e.nombre,
      etiqueta: e.ciudad || 'Sin ciudad',
      filas: [{
        icono: MapPin,
        texto: e.direccion || 'Sin dirección registrada'
      }, {
        icono: Phone,
        texto: e.telefono || 'Sin teléfono registrado'
      }, {
        icono: Container,
        texto: permitidas.length ? `Colas: ${permitidas.join(', ')}${segundas.length ? ` · 2ª opción: ${segundas.join(', ')}` : ''}` : `Admite todas las colas${segundas.length ? ` · 2ª opción: ${segundas.join(', ')}` : ''}`
      }],
      archivado: e.activo === false,
      alArchivar: () => archivar(e.nombre, 'Deja de aparecer como destino al armar una orden. Las órdenes ya registradas no cambian.', e.activo === false, () => onArchivarEnvasadora(e, e.activo !== false))
    };
  };
  const Icono = conf.icono;
  return <>
      <PageShell title="Flota y Destinos" sub="Conductores, camiones, colas y envasadoras, con las reglas de qué puede ir a dónde" action={canEdit ? <button onClick={showForm ? cerrarForm : abrirNuevo} style={primaryButton} data-id="element-595" data-mp="3l80bpdrvd5s5">
              {showForm ? <><X size={15} data-id="element-596" data-mp="3q6k0rn1rexoq" /> Cerrar</> : <><Plus size={15} data-id="element-597" data-mp="3u9zvgdu9qnys" /> Nuevo registro</>}
            </button> : undefined} data-id="element-594" data-mp="316xbdwpz6zmu">

        {/* Nada duele más que creer que guardaste y no haber guardado: si algo
            quedó solo en este navegador, se dice. Se reintenta solo al
            recargar, en cuanto las tablas existan. */}
        {pendientes > 0 ? <div style={{
        display: 'flex',
        gap: 11,
        alignItems: 'flex-start',
        padding: '13px 16px',
        marginBottom: 18,
        borderRadius: T.rMd,
        background: T.warnSoft,
        border: `1px solid ${T.warn}33`,
        color: T.warn,
        fontSize: 12.5,
        lineHeight: 1.5
      }} data-id="element-598" data-mp="ct8h5yg3e17l">

            <Archive size={16} style={{
          flexShrink: 0,
          marginTop: 1
        }} data-id="element-599" data-mp="2jmaojer7veg4" />
            <span data-id="element-600" data-mp="2no5l65y7d3nk">
              <strong data-id="element-601" data-mp="2g2jvs53i14o3">{pendientes} registro{pendientes > 1 ? 's' : ''} sin subir a Supabase.</strong>{' '}
              Están guardados en este navegador. Corre{' '}
              <code data-id="element-602" data-mp="2baa86vpuwgoz">sql/instalar_todo.sql</code> en el SQL Editor y recarga la
              página: se suben solos.
            </span>
          </div> : null}

        {/* ── PESTAÑAS ── */}
        <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 20
      }} data-id="element-603" data-mp="22cgdtqbnjx4g">

          {TABS.map(t => {
          const activa = tab === t.id;
          const IconoTab = t.icono;
          return <button key={t.id} type="button" onClick={() => cambiarTab(t.id)} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',
            borderRadius: 99,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
            color: activa ? t.color : T.textSecondary,
            background: activa ? t.soft : 'transparent',
            border: `1px solid ${activa ? `${t.color}44` : T.border}`,
            transition: 'all .15s'
          }} data-id="element-604" data-mp="2u0i9r9dwqz32">

                <IconoTab size={15} data-id="element-605" data-mp="gnoyk4zdmmod" />
                {t.label}
                <span style={{
              fontSize: 11,
              fontWeight: 800,
              padding: '1px 7px',
              borderRadius: 99,
              background: activa ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
              color: activa ? t.color : T.textMuted
            }} data-id="element-606" data-mp="gozzzy5cnicb">

                  {conteos[t.id]}
                </span>
              </button>;
        })}
        </div>

        {/* ── FORMULARIO ── */}
        <AnimatePresence data-id="element-607" data-mp="umeql67c9w5v">
          {showForm && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} style={{
          overflow: 'hidden',
          marginBottom: 22
        }} data-id="element-608" data-mp="unpwpco9bg05">

              <div style={{
            ...glassLight,
            padding: isMobile ? 18 : 24
          }} data-id="element-609" data-mp="1bwsexz5itfpr">

                <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 18
            }} data-id="element-610" data-mp="1gxg6clxnvyki">

                  <div style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                flexShrink: 0,
                background: conf.soft,
                border: `1px solid ${conf.color}30`,
                color: conf.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }} data-id="element-611" data-mp="1747qze9apwu5">

                    <Icono size={16} data-id="element-612" data-mp="o76ihjgj9elg" />
                  </div>
                  <div style={{
                fontSize: 15,
                fontWeight: 800,
                color: T.text
              }} data-id="element-613" data-mp="2eikn5wrxw4l6">

                    {editingId ? `Editar ${conf.singular}` : conf.nuevo}
                  </div>
                </div>

                <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0,1fr))',
              gap: 16,
              marginBottom: 16
            }} data-id="element-614" data-mp="3rf9oax7nik5c">

                  {tab === 'conductores' && <>
                      <Field label="Nombre completo *" data-id="element-615" data-mp="3dj67q6ppaabc">
                        <CampoValidado modo="letras" valor={form.nombre} onChange={v => set({
                    nombre: v
                  })} placeholder="Nombre del conductor" maxLength={60} data-id="element-616" data-mp="e2hq4zaueu6z" />

                      </Field>
                      <Field label="Pase del chofer" data-id="element-617" data-mp="59e6uadinn4f">
                        <CampoValidado modo="codigo" valor={form.pase} onChange={v => set({
                    pase: v
                  })} placeholder="APP-87" maxLength={15} data-id="element-618" data-mp="bcmzn89njrw" />

                      </Field>
                      <Field label="Teléfono" data-id="element-619" data-mp="t0a4vp8kfek4">
                        <CampoValidado modo="telefono" valor={form.telefono} onChange={v => set({
                    telefono: v
                  })} placeholder="(809) 000-0000" data-id="element-620" data-mp="syfis7lk5geg" />

                      </Field>
                    </>}

                  {tab === 'camiones' && <>
                      <Field label="Nombre del camión *" data-id="element-621" data-mp="1a89mhc2pfxgm">
                        <input style={inputStyle} value={form.nombre} onChange={e => set({
                    nombre: e.target.value.toUpperCase()
                  })} placeholder="MACK VISION" maxLength={40} data-id="element-622" data-mp="1eaxzzx2d5sjx" />

                      </Field>
                      <Field label="Placa *" data-id="element-623" data-mp="1o82dz9m22hpn">
                        <CampoValidado modo="placa" valor={form.placa} onChange={v => set({
                    placa: v
                  })} placeholder="L-259163" data-id="element-624" data-mp="1o6r89491uzvw" />

                      </Field>
                      <Field label="Solo lo puede llevar" data-id="element-625" data-mp="1y0q5bou9ntgz">
                        <select style={inputStyle} value={form.soloConductorId} onChange={e => set({
                    soloConductorId: e.target.value
                  })} data-id="element-626" data-mp="221tjm6wo1hg3">

                          <option value="" data-id="element-627" data-mp="1tc33b1tj3en7">Cualquier conductor</option>
                          {conductoresActivos.map(c => <option key={c.id} value={c.id} data-id="element-628" data-mp="1pc2qflm2vbi9">
                              {c.nombre}
                            </option>)}
                        </select>
                      </Field>
                    </>}

                  {tab === 'colas' && <>
                      <Field label="Ficha *" data-id="element-629" data-mp="1ae0n9ciyks1j">
                        <CampoValidado modo="codigo" valor={form.ficha} onChange={v => set({
                    ficha: v
                  })} placeholder="58-01" maxLength={15} data-id="element-630" data-mp="1aes57kjmur9c" />

                      </Field>
                      <Field label="Galones por viaje" data-id="element-631" data-mp="1kdrft8nykwyz">
                        <CampoValidado modo="numeros" valor={form.capacidad} onChange={v => set({
                    capacidad: v
                  })} placeholder="9010" maxLength={7} data-id="element-632" data-mp="1fck9zf5dm7uy" />

                      </Field>
                      <Field label="Descripción" data-id="element-633" data-mp="2qr78n0clsnw3">
                        <input style={inputStyle} value={form.descripcion} onChange={e => set({
                    descripcion: e.target.value
                  })} placeholder="Opcional" maxLength={60} data-id="element-634" data-mp="2qumoi5e3q0yk" />

                      </Field>
                    </>}

                  {tab === 'envasadoras' && <>
                      <Field label="Nombre *" data-id="element-635" data-mp="8iw7tbjgedm6">
                        <input style={inputStyle} value={form.nombre} onChange={e => set({
                    nombre: e.target.value.toUpperCase()
                  })} placeholder="HERRADURA GAS" maxLength={60} data-id="element-636" data-mp="8jp4tksm1or4" />

                      </Field>
                      <Field label="Ciudad" data-id="element-637" data-mp="10bwpcwb8llas">
                        <input style={inputStyle} value={form.ciudad} onChange={e => set({
                    ciudad: e.target.value
                  })} placeholder="Santiago" maxLength={40} data-id="element-638" data-mp="mhm6gvlx8lcn" />

                      </Field>
                      <Field label="RNC" data-id="element-639" data-mp="1f61gt4u3yfhn">
                        <CampoValidado modo="rnc" valor={form.rnc} onChange={v => set({
                    rnc: v
                  })} placeholder="1-30-16152-6" data-id="element-640" data-mp="1jxt0cveujkya" />

                      </Field>
                      <Field label="Teléfono" data-id="element-641" data-mp="1ov83v6b4vvba">
                        <CampoValidado modo="telefono" valor={form.telefono} onChange={v => set({
                    telefono: v
                  })} placeholder="(809) 000-0000" data-id="element-642" data-mp="1ovhvszfqocjm" />

                      </Field>
                      <Field label="Dirección" data-id="element-643" data-mp="1ewiygd0fc3bx">
                        <input style={inputStyle} value={form.direccion} onChange={e => set({
                    direccion: e.target.value
                  })} placeholder="Carretera, km, sector" maxLength={120} data-id="element-644" data-mp="1avpajjmzeoj1" />

                      </Field>
                    </>}
                </div>

                {/* Las asignaciones del conductor van a todo lo ancho y fuera
                    de la rejilla de dos columnas: cada una es una lista que
                    crece, y en media columna los renglones se estrangulan. */}
                {tab === 'conductores' && <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0,1fr))',
              gap: isMobile ? 20 : 18,
              marginBottom: 18,
              paddingTop: 16,
              borderTop: `1px solid ${T.border}`
            }} data-id="element-645" data-mp="n6eci9x0n88l">

                    <BloqueLista label="Camiones asignados" valores={form.camionIds} vacio={camionesActivos.length ? 'Ninguno: podrá llevar cualquiera.' : 'No hay camiones registrados.'} onAgregar={() => agregarEn('camionIds')} onQuitar={i => quitarDe('camionIds', i)} textoAgregar="Agregar camión" render={(valor, i) => <select style={inputStyle} value={valor} onChange={e => fijarEn('camionIds', i, e.target.value)} data-id="element-647" data-mp="2k1phrf98uz1b">
                          <option value="" data-id="element-648" data-mp="2ttljr1egb9at">— Elige el camión —</option>
                          {camionesActivos.map(c => <option key={c.id} value={c.id} data-id="element-649" data-mp="1wer2um0rvz5i">
                              {c.nombre} · {c.placa}
                            </option>)}
                        </select>} data-id="element-646" data-mp="37rtm5ou9b2td" />


                    <BloqueLista label="Colas asignadas" valores={form.colaIds} vacio={colasActivas.length ? 'Ninguna: podrá llevar cualquiera.' : 'No hay colas registradas.'} onAgregar={() => agregarEn('colaIds')} onQuitar={i => quitarDe('colaIds', i)} textoAgregar="Agregar cola" render={(valor, i) => <select style={inputStyle} value={valor} onChange={e => fijarEn('colaIds', i, e.target.value)} data-id="element-651" data-mp="13otui59b80f6">
                          <option value="" data-id="element-652" data-mp="1dp46xnnit3ta">— Elige la cola —</option>
                          {colasActivas.map(c => <option key={c.id} value={c.id} data-id="element-653" data-mp="18q11i23movks">
                              {c.ficha}{c.capacidadGalones ? ` · ${fmtGal(c.capacidadGalones)} gal` : ''}
                            </option>)}
                        </select>} data-id="element-650" data-mp="uq8eagrb8xhk" />

                  </div>}

                {/* ── REGLAS ── */}
                {tab === 'camiones' && <div style={{
              marginBottom: 18,
              paddingTop: 16,
              borderTop: `1px solid ${T.border}`
            }} data-id="element-654" data-mp="1wf2em2neouwe">

                    <Fichas label="Plantas a las que NO puede ir" ayuda="Marca las envasadoras vetadas para este camión. Sin nada marcado, puede ir a todas." opciones={envasadorasActivas.map(e => ({
                id: e.id,
                texto: e.nombre
              }))} seleccion={form.plantasVetadas} onToggle={id => alternar('plantasVetadas', id)} color={T.danger} data-id="element-655" data-mp="1reeemrh793q1" />

                  </div>}

                {tab === 'envasadoras' && <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0,1fr))',
              gap: isMobile ? 20 : 18,
              marginBottom: 18,
              paddingTop: 16,
              borderTop: `1px solid ${T.border}`
            }} data-id="element-656" data-mp="1sbt2xfikcyux">

                    <Fichas label="Colas que admite" ayuda="Sin nada marcado, admite todas." opciones={colasActivas.map(c => ({
                id: c.id,
                texto: c.ficha
              }))} seleccion={form.colasPermitidas} onToggle={id => alternar('colasPermitidas', id)} data-id="element-657" data-mp="1nc6r9aidvqo8" />

                    <Fichas label="Colas de segunda opción" ayuda="Pueden ir, pero no son la primera elección: se ofrecen aparte." opciones={colasActivas.map(c => ({
                id: c.id,
                texto: c.ficha
              }))} seleccion={form.colasSegundaOpcion} onToggle={id => alternar('colasSegundaOpcion', id)} color={T.warn} data-id="element-658" data-mp="u1x76qtfj13y" />

                  </div>}

                {error ? <div style={{
              marginBottom: 14,
              padding: '11px 14px',
              borderRadius: 12,
              background: T.dangerSoft,
              border: `1px solid ${T.danger}33`,
              color: T.danger,
              fontSize: 12.5
            }} data-id="element-659" data-mp="p8kkim8b94du">

                    {error}
                  </div> : null}

                <div style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap'
            }} data-id="element-660" data-mp="6cuemnx9bnuo">

                  <button type="button" onClick={guardar} style={primaryButton} data-id="element-661" data-mp="1wme483k2pfy8">
                    {editingId ? 'Guardar cambios' : 'Registrar'}
                  </button>
                  <button type="button" onClick={cerrarForm} style={ghostButton} data-id="element-662" data-mp="3l8o2cab79wr6">
                    Cancelar
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* ── BUSCADOR Y ARCHIVADOS ── */}
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: 18
      }} data-id="element-663" data-mp="3q6zbeito838c">

          <div style={{
          position: 'relative',
          flex: '1 1 240px'
        }} data-id="element-664" data-mp="8skhen8q62xd">

            <Search size={15} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-665" data-mp="3tzfmjdtrb6t" />

            <input style={{
            ...inputStyle,
            paddingLeft: 34
          }} placeholder={`Buscar en ${conf.label.toLowerCase()}`} value={busqueda} onChange={e => setBusqueda(e.target.value)} data-id="element-666" data-mp="2jisqkiu6fau3" />

          </div>

          {/* Aquí no se borra nada, así que hace falta poder ver lo archivado
              para devolverlo a servicio. */}
          <button type="button" onClick={() => {
          setVerArchivados(v => !v);
          cerrarForm();
        }} style={{
          padding: '9px 15px',
          borderRadius: 99,
          fontSize: 12.5,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          whiteSpace: 'nowrap',
          color: verArchivados ? T.warn : T.textSecondary,
          background: verArchivados ? T.warnSoft : 'transparent',
          border: `1px solid ${verArchivados ? `${T.warn}44` : T.border}`,
          transition: 'all .15s'
        }} data-id="element-667" data-mp="ngfldnerxfxo">

            <Archive size={14} data-id="element-668" data-mp="1k6f2w7cwjose" />
            {verArchivados ? 'Viendo archivados' : 'Ver archivados'}
            <span style={{
            fontSize: 11,
            fontWeight: 800,
            padding: '1px 7px',
            borderRadius: 99,
            background: verArchivados ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
            color: verArchivados ? T.warn : T.textMuted
          }} data-id="element-669" data-mp="1p4gn4vm2du5g">

              {totalArchivados}
            </span>
          </button>
        </div>

        {/* ── LISTADO ── */}
        {items.length === 0 ? <EmptyState title={busqueda ? `Ningún registro coincide` : `Sin ${conf.label.toLowerCase()} registrados`} hint={busqueda ? 'Prueba con otro texto.' : tab === 'envasadoras' ? 'Registra las plantas a las que despachas para poder elegirlas como destino.' : 'Registra el primero para poder elegirlo al armar una orden.'} icon={<Icono size={22} data-id="element-671" data-mp="3jj3xnffmtlyq" />} data-id="element-670" data-mp="2vou67htwcvnq" /> : <MotionGrid style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))',
        gap: 14
      }} data-id="element-672" data-mp="3em587a6wy36v">

            {items.map((x: any) => {
          const t = tarjeta(x);
          return <MotionItem key={t.id} style={{
            ...glassLight,
            padding: 0,
            overflow: 'hidden'
          }} data-id="element-673" data-mp="b550iagxbujn">

                  <div style={{
              padding: '18px 20px 16px'
            }} data-id="element-674" data-mp="3qxeaz549sgd7">

                    <div style={{
                display: 'flex',
                gap: 13,
                alignItems: 'flex-start',
                marginBottom: 13
              }} data-id="element-675" data-mp="3vvo8vnhdpv2i">

                      <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  flexShrink: 0,
                  background: conf.soft,
                  border: `1px solid ${conf.color}30`,
                  color: conf.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }} data-id="element-676" data-mp="3mu87g58jl3af">

                        <Icono size={18} data-id="element-677" data-mp="xcr1ngkrt9r2" />
                      </div>
                      <div style={{
                  minWidth: 0,
                  flex: 1
                }} data-id="element-678" data-mp="xbfxvgrbs1zy">

                        <div style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: T.text,
                    lineHeight: 1.3,
                    wordBreak: 'break-word'
                  }} data-id="element-679" data-mp="125k6s5k5ik3o">

                          {t.titulo}
                        </div>
                        <span style={{
                    display: 'inline-block',
                    marginTop: 6,
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '.04em',
                    textTransform: 'uppercase',
                    padding: '3px 9px',
                    borderRadius: 99,
                    color: conf.color,
                    background: conf.soft,
                    border: `1px solid ${conf.color}30`
                  }} data-id="element-680" data-mp="1g8zm440lf5rj">

                          {t.etiqueta}
                        </span>
                      </div>
                    </div>

                    <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                fontSize: 12.5,
                color: T.textSecondary
              }} data-id="element-681" data-mp="28wlsfk17goi3">

                      {t.filas.map((f, i) => <Dato key={i} icono={f.icono} data-id="element-682" data-mp="2l1srbed113hw" data-mpi={i}>
                          {f.texto}
                        </Dato>)}
                    </div>
                  </div>

                  {/* Los botones van en su propia fila: metidos junto al
                      título aplastan el nombre en cuanto la tarjeta se
                      estrecha. */}
                  {canEdit || canArchive ? <div style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              padding: '11px 20px',
              borderTop: `1px solid ${T.border}`,
              background: T.surface
            }} data-id="element-683" data-mp="229q4fl9skqz7">

                      {canEdit && !t.archivado ? <button type="button" onClick={() => editar(t.id)} style={{
                ...ghostButton,
                padding: '7px 13px',
                fontSize: 12.5
              }} data-id="element-684" data-mp="1ejm35kk2t3bd">

                          <Pencil size={13} data-id="element-685" data-mp="1byocysnr8rh7" /> Editar
                        </button> : null}
                      {canArchive ? <button type="button" onClick={t.alArchivar} style={{
                ...ghostButton,
                padding: '7px 13px',
                fontSize: 12.5,
                color: t.archivado ? T.info : T.warn,
                borderColor: t.archivado ? `${T.info}44` : `${T.warn}44`,
                background: t.archivado ? T.infoSoft : T.warnSoft
              }} data-id="element-686" data-mp="16ya4j9i8rfin">

                          {t.archivado ? <><ArchiveRestore size={13} data-id="element-687" data-mp="o80r13h081bn" /> Devolver a servicio</> : <><Archive size={13} data-id="element-688" data-mp="jbk58fxa1z1q" /> Archivar</>}
                        </button> : null}
                    </div> : null}
                </MotionItem>;
        })}
          </MotionGrid>}
      </PageShell>
      {dialogo}
    </>;
}